'use client';

import Link from 'next/link';
import {
  ArrowRight,
  BookOpen,
  Camera,
  Car,
  ChevronRight,
  Gift,
  Globe,
  HelpCircle,
  Images,
  MapPinned,
  UserPlus,
} from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';

type LinkItem = {
  icon: React.ReactNode;
  label: string;
  href: string;
  description?: string;
  external?: boolean;
};

function AppleIcon() {
  return (
    <svg className="h-5 w-5 text-dgd-charcoal" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

function GooglePlayIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden>
      <path d="M3.4 1.6c-.3.2-.4.6-.4 1v18.8c0 .4.2.7.4.9l10.6-10.6L3.4 1.6z" fill="#00d4ff" />
      <path d="M17.7 8.6 4.6 1.2c-.4-.2-.7-.2-1 0l10.6 10.5 3.5-3.1z" fill="#00f176" />
      <path d="m17.7 15.4-3.5-3.1L3.6 22.8c.3.2.6.2 1 0l13.1-7.4z" fill="#ff3a44" />
      <path d="m21 11-3.3-1.9-3.7 2.9 3.7 2.9 3.3-1.9c1-.6 1-2 0-2z" fill="#ffce00" />
    </svg>
  );
}

const ICON_CLASS = 'h-5 w-5 text-dgd-gold-hover';

const QUICK_LINKS: LinkItem[] = [
  {
    icon: <UserPlus className={ICON_CLASS} aria-hidden />,
    label: 'Register Now',
    href: '/register',
    description: 'Sign up for your nearest drive',
  },
  {
    icon: <Globe className={ICON_CLASS} aria-hidden />,
    label: 'Live Drive Tracker',
    href: '/live',
    description: 'Follow drives around the world',
  },
  {
    icon: <AppleIcon />,
    label: 'Download for iOS',
    href: 'https://apps.apple.com/us/app/gentlemans-drive/id6569228734',
    description: 'DGD App on the App Store',
    external: true,
  },
  {
    icon: <GooglePlayIcon />,
    label: 'Download for Android',
    href: 'https://play.google.com/store/apps/details?id=com.gentlemansdrive.android.app',
    description: 'DGD App on Google Play',
    external: true,
  },
  {
    icon: <Car className={ICON_CLASS} aria-hidden />,
    label: 'Vehicle Eligibility',
    href: '/vehicles',
    description: 'Which cars can take part',
  },
  {
    icon: <BookOpen className={ICON_CLASS} aria-hidden />,
    label: 'Keeping it Dapper',
    href: '/dapper',
    description: 'What to wear on drive day',
  },
  {
    icon: <Camera className={ICON_CLASS} aria-hidden />,
    label: 'Upload Photos & Videos',
    href: '/content-upload',
    description: 'Share your drive day content',
  },
  {
    icon: <Images className={ICON_CLASS} aria-hidden />,
    label: 'Gallery',
    href: '/gallery',
    description: 'Photos from past drives',
  },
  {
    icon: <MapPinned className={ICON_CLASS} aria-hidden />,
    label: 'Change Drive City',
    href: '/my/events',
    description: 'Manage events on your profile',
  },
  {
    icon: <Gift className={ICON_CLASS} aria-hidden />,
    label: 'Rewards & Orders',
    href: '/my/orders',
    description: 'Pins and fundraising rewards',
  },
  {
    icon: <HelpCircle className={ICON_CLASS} aria-hidden />,
    label: 'FAQs',
    href: '/faq',
    description: 'Other questions answered',
  },
];

const CARD_CLASS =
  'group flex items-center gap-4 rounded-xl border border-dgd-charcoal/10 bg-white px-4 py-3 shadow-sm transition hover:border-dgd-gold/60 hover:shadow-md';

export function DriveDayPage() {
  const { isAuthenticated, registrationStatus } = useAuth({ hydrateFromQuery: true });

  const registeredEventName =
    isAuthenticated && registrationStatus?.eventName ? registrationStatus.eventName : null;
  const primaryLabel = registeredEventName
    ? `View ${registeredEventName} Details`
    : 'View Full Drive Details';

  return (
    <div className="relative min-h-[80vh] overflow-hidden bg-dgd-cream">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-dgd-gold/25 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 top-40 h-80 w-80 rounded-full bg-dgd-gold/15 blur-3xl"
      />

      <div className="relative mx-auto w-full max-w-md px-5 pb-12 pt-28 sm:pb-16 sm:pt-36">
        <header className="text-center">
          <h1 className="font-dgd-display text-5xl uppercase leading-[0.95] tracking-tight text-dgd-charcoal sm:text-6xl">
            DGD <span className="text-dgd-gold-hover">Quick Links</span>
          </h1>
        </header>

        <Link
          href="/my/dashboard"
          className="mt-7 flex items-center justify-between gap-3 rounded-xl border border-dgd-gold/40 bg-dgd-gold px-5 py-4 text-dgd-charcoal shadow-sm transition hover:bg-dgd-gold-hover hover:text-white"
        >
          <span className="flex flex-col items-start min-w-0">
            <span className="text-base font-semibold truncate max-w-full">{primaryLabel}</span>
            <span className="text-[11px] uppercase tracking-wider opacity-70">
              {registeredEventName ? 'Your Drive' : 'My Dashboard'}
            </span>
          </span>
          <ArrowRight className="h-5 w-5 shrink-0" />
        </Link>

        <ul className="mt-6 space-y-2">
          {QUICK_LINKS.map((item) => {
            const content = (
              <>
                <span
                  aria-hidden
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-dgd-charcoal/10 bg-dgd-cream"
                >
                  {item.icon}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-dgd-charcoal">{item.label}</span>
                  {item.description ? (
                    <span className="block truncate text-[11px] text-stone-500">
                      {item.description}
                    </span>
                  ) : null}
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-stone-400 transition group-hover:text-dgd-gold-hover" />
              </>
            );

            return (
              <li key={item.href}>
                {item.external ? (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={CARD_CLASS}
                  >
                    {content}
                  </a>
                ) : (
                  <Link href={item.href} className={CARD_CLASS}>
                    {content}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>

        <div className="mt-8 space-y-3 rounded-xl border border-dgd-charcoal/10 bg-white/70 p-4 text-[12px] leading-relaxed text-stone-600">
          <p>
            <strong className="text-dgd-charcoal">Missing pins or rewards?</strong> Rewards are
            dispatched weekly via regular post. Check{' '}
            <Link
              href="/my/orders"
              className="text-dgd-gold-hover underline-offset-2 hover:underline"
            >
              My Orders
            </Link>{' '}
            for tracking updates. Please allow up to 4 weeks for delivery.
          </p>
          <p>
            <strong className="text-dgd-charcoal">Missing donation?</strong> If you accidentally
            donated as a general donation, we can manually assign it to your profile after the drive
            weekend.
          </p>
        </div>
      </div>
    </div>
  );
}
